import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import "../../styles/admin/admin-detail.css";
import "../../styles/admin/admin-form.css";
import config from "../../config";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

export default function AdminUserDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const [emailMessageType, setEmailMessageType] = useState("");

  useEffect(() => {
    loadUser();
  }, [id]);

  const loadUser = async () => {
    setLoading(true);
    setError("");
    setEmailMessage("");
    setEmailMessageType("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/users/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo cargar el detalle del usuario");
      }

      const data = responseText ? JSON.parse(responseText) : null;
      setUser(data);
    } catch (err) {
      setError(err.message || "Ocurrió un error al cargar el usuario");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSendPendingPaymentEmail = async () => {
    setSendingEmail(true);
    setEmailMessage("");
    setEmailMessageType("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/admin/users/${id}/send-pending-payment-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo enviar el correo");
      }

      setEmailMessage(responseText || "Correo enviado correctamente.");
      setEmailMessageType("success");
    } catch (err) {
      setEmailMessage(err.message || "Ocurrió un error al enviar el correo.");
      setEmailMessageType("error");
    } finally {
      setSendingEmail(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("es-CO");
    } catch {
      return "-";
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-page-head">
          <div>
            <h2>Detalle de usuario</h2>
            <p>Cargando información...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="admin-page">
        <div className="admin-page-head">
          <div>
            <h2>Detalle de usuario</h2>
            <p>Información general del usuario</p>
          </div>
        </div>

        <p className="admin-form-message error">{error}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="admin-page">
        <div className="admin-page-head">
          <div>
            <h2>Detalle de usuario</h2>
            <p>No se encontró información del usuario</p>
          </div>
        </div>
      </div>
    );
  }

  const fullName = `${user.name || ""} ${user.lastName || ""}`.trim();

  const normalizedStatus = String(user.status ?? "")
    .normalize("NFKC")
    .toUpperCase();

  const showPendingButton = normalizedStatus.includes("PENDING_PAYMENT");

  return (
    <div className="admin-page">
      <div className="admin-page-head admin-detail-topbar">
        <div className="admin-detail-head-left">
          <button
            type="button"
            className="admin-back-arrow-btn"
            onClick={() => navigate("/admin/usuarios")}
            title="Volver"
          >
            <ArrowLeft size={20} />
          </button>

          <div>
            <h2>Detalle de usuario</h2>
            <p>Información general del usuario</p>
          </div>
        </div>
      </div>

      {error ? <p className="admin-form-message error">{error}</p> : null}

      <div className="detail-grid">
        <section className="detail-card">
          <h3>Datos personales</h3>
          <div className="detail-list">
            <p><strong>Nombre:</strong> {fullName || "-"}</p>
            <p><strong>Correo:</strong> {user.email || "-"}</p>
            <p><strong>Celular:</strong> {user.phone || "-"}</p>
            <p><strong>Cédula:</strong> {user.dni || "-"}</p>
            <p><strong>País:</strong> {user.country || "-"}</p>
            <p><strong>Fecha de registro:</strong> {formatDate(user.registerDate)}</p>
          </div>
        </section>

        <section className="detail-card">
          <h3>Mascotas</h3>
          <div className="detail-list">
            <p><strong>Cantidad de mascotas:</strong> {user.petCount ?? 0}</p>
          </div>
        </section>

        <section className="detail-card">
          <h3>Estado de cuenta</h3>

          <div className="detail-list">
            <p><strong>Estado:</strong> {user.status || "-"}</p>
            <p><strong>Suscripción activa:</strong> {user.subscriptionActive ? "Sí" : "No"}</p>
            <p><strong>Próximo cobro:</strong> {formatDate(user.nextChargeAt)}</p>
            <p><strong>Proveedor de pago:</strong> {user.paymentProvider || "-"}</p>
          </div>

          {showPendingButton && (
            <div className="admin-detail-inline-actions">
              <button
                type="button"
                className="admin-send-mail-btn"
                onClick={handleSendPendingPaymentEmail}
                disabled={sendingEmail}
              >
                {sendingEmail ? "Enviando..." : "Enviar correo"}
              </button>
            </div>
          )}

          {emailMessage && (
            <p className={`admin-inline-message ${emailMessageType}`}>
              {emailMessage}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}